import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  usePlaidLink,
  type PlaidLinkOnSuccess,
  type PlaidLinkOnExit,
} from 'react-plaid-link';
import { apiFetch } from '../auth/apiFetch';

interface ConnectBankButtonProps {
  onConnected: () => void;
  className?: string;
  children?: React.ReactNode;
}

// The redirect URI we send to Plaid + register in the dashboard. Must match
// EXACTLY what's allowlisted in Plaid → Team Settings → API → Allowed
// redirect URIs. Using location.origin keeps preview / prod in sync without
// env-var ceremony, but each origin still needs its own entry in the
// dashboard.
//
// Returns null on http:// origins (i.e. local dev) — Plaid REQUIRES HTTPS
// for the redirect_uri field even in sandbox, so passing the http URL would
// 400 the link-token call. OAuth banks can't complete on http anyway.
// Non-OAuth banks work fine without the redirect_uri.
function plaidRedirectUri(): string | null {
  if (window.location.protocol !== 'https:') return null;
  return `${window.location.origin}/settings`;
}

// SessionStorage key for the linkToken. Plaid's OAuth flow re-launches the
// page; the post-redirect handler needs the SAME linkToken that started the
// session, so we cache it across the redirect. SessionStorage (not local)
// auto-clears on tab close — fine for a one-shot flow.
const LINK_TOKEN_STORAGE_KEY = 'plaid:link-token';

async function fetchLinkToken(): Promise<string> {
  const redirectUri = plaidRedirectUri();
  const res = await apiFetch('/api/plaid/link-token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(redirectUri ? { redirectUri } : {}),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`link-token ${res.status}: ${body.slice(0, 200)}`);
  }
  const data = (await res.json()) as { linkToken: string };
  return data.linkToken;
}

async function exchangePublicToken(
  publicToken: string,
  institution: { name: string | null; institution_id: string | null } | null
): Promise<void> {
  const res = await apiFetch('/api/plaid/exchange', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ publicToken, institution }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`exchange ${res.status}: ${body.slice(0, 200)}`);
  }
}

export function ConnectBankButton({
  onConnected,
  className,
  children,
}: ConnectBankButtonProps) {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exchanging, setExchanging] = useState(false);

  // OAuth resume: when the user returns from a bank's OAuth approval,
  // Plaid appends `?oauth_state_id=…` to the redirect URL. Detect that on
  // mount and (a) reuse the cached linkToken from before the redirect,
  // (b) pass `receivedRedirectUri` to usePlaidLink so it can finish the
  // flow without re-prompting.
  const isOAuthRedirect = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return new URL(window.location.href).searchParams.has('oauth_state_id');
  }, []);
  const receivedRedirectUri = isOAuthRedirect ? window.location.href : undefined;

  useEffect(() => {
    let cancelled = false;
    if (isOAuthRedirect) {
      // Pull the cached linkToken from before the redirect. If it's missing
      // (e.g. tab closed mid-flow) the user has to start over — surface a
      // friendly message rather than silently doing nothing.
      const cached = sessionStorage.getItem(LINK_TOKEN_STORAGE_KEY);
      if (cached) {
        setLinkToken(cached);
      } else {
        setError('OAuth session expired. Click Connect a bank to start over.');
      }
      return;
    }
    fetchLinkToken()
      .then((t) => {
        if (cancelled) return;
        setLinkToken(t);
        // Cache for the OAuth redirect path; cleared in onSuccess / onExit.
        sessionStorage.setItem(LINK_TOKEN_STORAGE_KEY, t);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [isOAuthRedirect]);

  const clearOAuthState = useCallback(() => {
    sessionStorage.removeItem(LINK_TOKEN_STORAGE_KEY);
    if (isOAuthRedirect) {
      // Strip the OAuth query params so a manual refresh doesn't try to
      // resume an already-completed flow.
      const url = new URL(window.location.href);
      url.searchParams.delete('oauth_state_id');
      window.history.replaceState({}, '', url.toString());
    }
  }, [isOAuthRedirect]);

  const onSuccess = useCallback<PlaidLinkOnSuccess>(
    (publicToken, metadata) => {
      setExchanging(true);
      setError(null);
      exchangePublicToken(publicToken, {
        name: metadata.institution?.name ?? null,
        institution_id: metadata.institution?.institution_id ?? null,
      })
        .then(() => {
          clearOAuthState();
          onConnected();
        })
        .catch((err: Error) => {
          setError(err.message);
        })
        .finally(() => {
          setExchanging(false);
        });
    },
    [onConnected, clearOAuthState]
  );

  const onExit = useCallback<PlaidLinkOnExit>(
    (err) => {
      if (err) setError(err.display_message ?? err.error_message ?? 'connection cancelled');
      clearOAuthState();
    },
    [clearOAuthState]
  );

  const { open, ready } = usePlaidLink({
    token: linkToken,
    receivedRedirectUri,
    onSuccess,
    onExit,
  });

  // Auto-open Link to finish the OAuth flow as soon as both the cached
  // linkToken and Plaid's SDK are ready.
  useEffect(() => {
    if (isOAuthRedirect && ready && linkToken && !exchanging) open();
  }, [isOAuthRedirect, ready, linkToken, exchanging, open]);

  const disabled = !ready || !linkToken || exchanging;

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => open()}
        disabled={disabled}
        className={className ?? 'btn btn-primary'}
      >
        {exchanging ? 'Saving…' : (children ?? 'Connect a bank')}
      </button>
      {error && <p className="text-sm text-red">{error}</p>}
    </div>
  );
}
